import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Megaphone, Plus, Pencil, Trash2, Loader2, Pin, Info, AlertTriangle, AlertOctagon, EyeOff, Eye,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const empty = { title: "", message: "", priority: "information", published: true, pinned: false, start_date: "", end_date: "" };

export const PRIORITY_META = {
  information: { label: "Information", icon: Info, cls: "text-accent border-accent/40 bg-accent/10" },
  warning: { label: "Warning", icon: AlertTriangle, cls: "text-warning border-warning/40 bg-warning/10" },
  important: { label: "Important", icon: AlertOctagon, cls: "text-destructive border-destructive/40 bg-destructive/10" },
};

const fmtDate = (d) => { try { return format(new Date(d), "dd MMM yyyy"); } catch { return d; } };

export default function Announcements() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    try { const { data } = await api.get("/announcements"); setItems(data); }
    catch { toast.error("Failed to load announcements"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title || "", message: a.message || "", priority: a.priority || "information",
      published: !!a.published, pinned: !!a.pinned, start_date: a.start_date || "", end_date: a.end_date || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, start_date: form.start_date || null, end_date: form.end_date || null };
      if (editing) { await api.put(`/announcements/${editing.id}`, payload); toast.success("Announcement updated"); }
      else { await api.post("/announcements", payload); toast.success("Announcement created"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    try { await api.delete(`/announcements/${a.id}`); toast.success("Announcement deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const togglePublish = async (a) => {
    try { await api.put(`/announcements/${a.id}`, { published: !a.published }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="announcements-page">
      <PageHeader overline="Communication" title="Announcements" description="Broadcast maintenance notices, server changes and important updates to all resellers.">
        <Button onClick={openCreate} data-testid="add-announcement-button"><Plus className="h-4 w-4 mr-2" /> New Announcement</Button>
      </PageHeader>

      {!items ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}</div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-border">
          <Megaphone className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No announcements yet. Create one to notify your resellers.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const meta = PRIORITY_META[a.priority] || PRIORITY_META.information;
            const Icon = meta.icon;
            return (
              <Card key={a.id} className={`p-5 border-border bg-card ${!a.published ? "opacity-60" : ""}`} data-testid={`announcement-row-${a.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`grid place-items-center h-10 w-10 rounded-md border shrink-0 ${meta.cls}`}><Icon className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-semibold text-lg truncate">{a.title}</h3>
                      {a.pinned && <Badge variant="outline" className="border-primary/40 text-primary gap-1"><Pin className="h-3 w-3" /> Pinned</Badge>}
                      <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                      {!a.published && <Badge variant="secondary" className="gap-1"><EyeOff className="h-3 w-3" /> Hidden</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5 whitespace-pre-wrap">{a.message}</p>
                    {(a.start_date || a.end_date) && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {a.start_date ? `From ${fmtDate(a.start_date)}` : "Active now"}{a.end_date ? ` · until ${fmtDate(a.end_date)}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" title={a.published ? "Hide" : "Publish"} onClick={() => togglePublish(a)} data-testid={`announcement-toggle-${a.id}`}>
                      {a.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`announcement-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`announcement-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-popover">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete announcement?</AlertDialogTitle>
                          <AlertDialogDescription>"{a.title}" will be removed for all resellers. This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(a)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Announcement" : "New Announcement"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={set("title")} placeholder="Scheduled maintenance" data-testid="announcement-title" /></div>
            <div className="space-y-2"><Label>Message</Label><Textarea value={form.message} onChange={set("message")} rows={4} placeholder="Servers will be upgraded on…" data-testid="announcement-message" /></div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="announcement-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="information">Information</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Start date <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="announcement-start" /></div>
              <div className="space-y-2"><Label>End date <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="date" value={form.end_date} onChange={set("end_date")} data-testid="announcement-end" /></div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div><Label className="cursor-pointer">Published</Label><p className="text-xs text-muted-foreground">Visible to resellers on their dashboard.</p></div>
              <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="announcement-published" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div><Label className="cursor-pointer">Pin to top</Label><p className="text-xs text-muted-foreground">Always shown first; resellers cannot dismiss it.</p></div>
              <Switch checked={form.pinned} onCheckedChange={(v) => setForm((f) => ({ ...f, pinned: v }))} data-testid="announcement-pinned" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving || !form.title || !form.message} data-testid="announcement-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
